import { watch, type Ref } from 'vue';

/**
 * Léo help assistant (Fondation module `CssFondation.Leo`, package `Core.Leo`).
 *
 * The launcher (`leo.js`) is served by the Léo server and owns its button, panel and labels, so
 * there is no component here: once the user is signed in, ask the API whether Léo is offered
 * (`GET /core/Leo/Configuration`), load the launcher from the host the API names, and start it
 * against the API's own session route. The service key never reaches the browser.
 *
 * Inert unless the server answers `enabled: true`: the module is off by default, and a user
 * without `Core.Leo.Use` gets a 403, treated like « not offered ».
 */

export type LeoLocale = 'fr-CA' | 'en-CA';

/** Minimal shape of the launcher exposed by `leo.js` as `window.Leo`. */
export interface LeoLauncher {
  start: (options: { sessionUrl: string; locale: LeoLocale; headers?: Record<string, string> }) => void;
}

declare global {
  interface Window {
    Leo?: LeoLauncher;
  }
}

export interface LeoConfiguration {
  enabled: boolean;
  url: string | null;
}

export interface LeoAssistantDeps {
  /** True once the user is signed in and the tenant initialized (permissions are wired up). */
  ready: Ref<boolean>;
  /** `GET /core/Leo/Configuration`; rejects when the user lacks `Core.Leo.Use`. */
  fetchConfiguration: () => Promise<LeoConfiguration>;
  /** Bearer token for the API, or null in cookie mode (OIDC BFF). */
  getAccessToken: () => Promise<string | null>;
  /** Base URL of the API, without trailing slash (e.g. `https://host/api`). */
  getApiUrl: () => string | undefined;
  getLocale: () => LeoLocale;
  /** Loads a script and resolves once it has run. Injectable for tests. */
  loadScript: (src: string) => Promise<void>;
  getLauncher?: () => LeoLauncher | undefined;
}

export function loadScriptTag(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(src));
    document.head.append(script);
  });
}

/** Starts Léo once the user is signed in. Best-effort: a failure never disturbs the app. */
export function installLeoAssistant(deps: LeoAssistantDeps): void {
  let started = false;

  async function start(): Promise<void> {
    if (started) return;
    started = true;
    try {
      const configuration = await deps.fetchConfiguration();
      const apiUrl = deps.getApiUrl();
      if (!configuration.enabled || !configuration.url || !apiUrl) return;

      const token = await deps.getAccessToken().catch(() => null);
      await deps.loadScript(`${configuration.url.replace(/\/+$/, '')}/leo.js`);
      const launcher = (deps.getLauncher ?? (() => window.Leo))();
      launcher?.start({
        sessionUrl: `${apiUrl.replace(/\/+$/, '')}/core/Leo/Session`,
        locale: deps.getLocale(),
        // i18n-ignore-next-line: HTTP authorization scheme, not user-visible text
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch (e) {
      // 403 (no Core.Leo.Use) or a Léo host that is unreachable: the app works without it.
      console.debug(e);
    }
  }

  watch(deps.ready, (ready) => {
    if (ready) void start();
  }, { immediate: true });
}
